// Cornerstone Caregiving site config. PDF URLs point at the enroll app's
// public/enroll-assets/ folder (stable, unhashed paths, only linked from the
// enroll app itself); the logo is an inlined data URL so it renders from any
// origin (see logoData.ts).
import type {GroupConfig} from '../types';
import {HOW_TO_ENROLL_WITH_CODE} from '../defaults/howToEnroll';
import {CORNERSTONE_LOGO_DATA_URL} from './logoData';

const cornerstoneLogo = CORNERSTONE_LOGO_DATA_URL;
const cornerstoneEnrollmentGuide = '/enroll-assets/cornerstone-enrollment-guide.pdf';
const cornerstonePlusSbc = '/enroll-assets/cornerstone-preventive-plus-benefits-summary.pdf';
const cornerstoneBronzeSbc = '/enroll-assets/cornerstone-bronze-benefits-summary.pdf';
const cornerstoneSilverSbc = '/enroll-assets/cornerstone-silver-benefits-summary.pdf';
const cornerstoneGoldSbc = '/enroll-assets/cornerstone-gold-benefits-summary.pdf';
const cornerstoneManagersFaq = '/enroll-assets/cornerstone-managers-faq.pdf';
const cornerstoneFlyer = '/enroll-assets/cornerstone-flyer.pdf';

export const cornerstoneConfig: GroupConfig = {
  slug: 'cornerstone',
  name: 'Cornerstone Caregiving',
  logoUrl: cornerstoneLogo,
  primaryColor: '#1F3D7A',
  heroHeadline: [
    {text: 'Top notch care. '},
    {text: 'Affordable', accent: true},
    {text: ' prices.'},
  ],
  heroTagline: 'Cornerstone Caregiving Wellness Benefits',
  heroBlurb:
    'Cornerstone Caregiving has partnered with Concierge to provide quality health benefits for caregivers — without breaking the bank.',
  accessCodeScreen: {
    headline: [{text: 'Welcome, '}, {text: 'Cornerstone', accent: true}, {text: ' caregivers.'}],
    blurb:
      "Cornerstone Caregiving partners with Concierge to provide quality health benefits for caregivers — without breaking the bank. That's the Concierge Promise.",
    placeholder: 'e.g., 00000',
  },
  banner: 'Caregiver Insurance',
  accessCodeTerm: 'AxisCare Code',
  benefitsContact: 'local office',
  coveragePeriod: 'January 1, 2026 — December 31, 2026',
  supportPhone: '888.820.5687',
  supportText: '918.876.5015',
  supportEmail: 'cs@ctpa.com',
  comparisonPdfUrl: cornerstoneEnrollmentGuide,
  products: [
    {
      category: 'medical',
      label: 'Medical',
      description:
        'ACA-compliant preventive coverage with virtual care, prescription benefits, and tiered medical add-ons. Pick one plan or decline.',
      planIds: ['preventive-plus', 'preventive-bronze', 'preventive-silver', 'preventive-gold'],
      declinable: true,
      benefitRows: [
        {
          category: 'preventive',
          label: 'ACA Preventive Services',
          values: {
            'preventive-plus': '100% covered · unlimited',
            'preventive-bronze': '100% covered · unlimited',
            'preventive-silver': '100% covered · unlimited',
            'preventive-gold': '100% covered · unlimited',
          },
        },
        {
          category: 'everyday',
          label: 'Virtual Care 24/7 (Clever Health)',
          values: {
            'preventive-plus': '$0 copay · unlimited',
            'preventive-bronze': '$0 copay · unlimited',
            'preventive-silver': '$0 copay · unlimited',
            'preventive-gold': '$0 copay · unlimited',
          },
        },
        {
          category: 'everyday',
          label: 'Primary Care Office Visit',
          values: {
            'preventive-plus': '$25 copay · 3 visits max',
            'preventive-bronze': '$25 copay · 5 visits max',
            'preventive-silver': '$25 copay · 5 visits max',
            'preventive-gold': '$25 copay · 5 visits max',
          },
        },
        {
          category: 'everyday',
          label: 'Specialist Office Visit',
          values: {
            'preventive-plus': null,
            'preventive-bronze': '$50 copay · 3 visits max',
            'preventive-silver': '$50 copay · 3 visits max',
            'preventive-gold': '$50 copay · 3 visits max',
          },
        },
        {
          category: 'everyday',
          label: 'Urgent Care Visit',
          values: {
            'preventive-plus': null,
            'preventive-bronze': '$75 copay · 3 visits max',
            'preventive-silver': '$75 copay · 3 visits max',
            'preventive-gold': '$75 copay · 3 visits max',
          },
        },
        {
          category: 'prescriptions',
          label: 'Prescription Plan',
          values: {
            'preventive-plus': 'Unlimited ACA Rx · $0 copay · formulary $0 copay unlimited scripts',
            'preventive-bronze':
              'Unlimited ACA Rx · $0 copay · formulary $0 copay unlimited scripts',
            'preventive-silver':
              'Unlimited ACA Rx · $0 copay · formulary $0 copay unlimited scripts',
            'preventive-gold': 'Unlimited ACA Rx · $0 copay · formulary $0 copay unlimited scripts',
          },
        },
        {
          category: 'specialty',
          label: 'Chiropractor (manipulation only)',
          values: {
            'preventive-plus': null,
            'preventive-bronze': null,
            'preventive-silver': '10 visits · $40 max per visit',
            'preventive-gold': '15 visits · $50 max per visit',
          },
        },
        {
          category: 'hospital',
          label: 'Accident Coverage',
          values: {
            'preventive-plus': null,
            'preventive-bronze': 'Up to $500',
            'preventive-silver': 'Up to $1,000',
            'preventive-gold': 'Up to $2,000',
          },
        },
        {
          category: 'hospital',
          label: 'Diagnostic, Lab & X-ray',
          values: {
            'preventive-plus': null,
            'preventive-bronze': 'Class I $30 ×2 · Class II $100 ×2 · Class III $100 ×1',
            'preventive-silver': 'Class I $30 ×2 · Class II $100 ×2 · Class III $100 ×1',
            'preventive-gold': 'Class I $30 ×2 · Class II $100 ×2 · Class III $100 ×1',
          },
        },
        {
          category: 'hospital',
          label: 'Hospitalization (in-patient)',
          values: {
            'preventive-plus': null,
            'preventive-bronze': '$500/day 1+ · $250 days 2–30',
            'preventive-silver': '$750/day 1+ · $375 days 2–30',
            'preventive-gold': '$1,000/day 1+ · $500 days 2–30',
          },
        },
        {
          category: 'hospital',
          label: 'Surgery (in/out-patient + anesthesia)',
          values: {
            'preventive-plus': null,
            'preventive-bronze': 'Surgery $500 · Anesthesia $125 · 1 day',
            'preventive-silver': 'Surgery $750 · Anesthesia $187.50 · 1 day',
            'preventive-gold': 'Surgery $1,000 · Anesthesia $250 · 1 day',
          },
        },
      ],
    },
    {
      category: 'dental',
      label: 'Dental',
      description: 'Preventive, basic, and major dental services through a single plan.',
      planIds: ['dental-1000'],
      declinable: true,
      declineStyle: 'skipped',
      benefitsTableTitle: "What's covered",
      benefitRows: [
        {category: 'plan', label: 'Annual maximum', values: {'dental-1000': '$1,000 per benefit year'}},
        {category: 'plan', label: 'Deductible', values: {'dental-1000': '$50 individual · $150 family'}},
        {category: 'plan', label: 'Providers', values: {'dental-1000': 'Any licensed dentist (no network)'}},
        {
          category: 'preventive',
          label: 'Preventive services',
          values: {'dental-1000': 'Covered 100% · no deductible · no waiting period'},
        },
        {category: 'preventive', label: 'Exams & cleanings', values: {'dental-1000': '2× per benefit year'}},
        {
          category: 'preventive',
          label: 'Fluoride (dependents under 18)',
          values: {'dental-1000': '2× per benefit year'},
        },
        {category: 'preventive', label: 'Sealants', values: {'dental-1000': 'Up to age 16'}},
        {
          category: 'preventive',
          label: 'X-rays',
          values: {'dental-1000': 'Bitewing series 1×/yr · full-mouth or panorex every 3 yrs'},
        },
        {
          category: 'basic',
          label: 'Basic services (fillings, oral surgery)',
          values: {'dental-1000': 'Covered 80% after deductible'},
        },
        {category: 'basic', label: 'Basic waiting period', values: {'dental-1000': '90 days'}},
        {
          category: 'major',
          label: 'Major services (crowns, dentures, bridges)',
          values: {'dental-1000': 'Covered 50% after deductible'},
        },
        {category: 'major', label: 'Major waiting period', values: {'dental-1000': '180 days'}},
      ],
    },
    {
      category: 'vision',
      label: 'Vision',
      description: 'Routine eye exams plus generous hardware allowance.',
      planIds: ['vision-600'],
      declinable: true,
      declineStyle: 'skipped',
      benefitsTableTitle: "What's covered",
      benefitRows: [
        {
          category: 'plan',
          label: 'Annual maximum',
          values: {'vision-600': '$600 per member · combined services'},
        },
        {category: 'plan', label: 'Deductible', values: {'vision-600': 'None'}},
        {category: 'plan', label: 'Providers', values: {'vision-600': 'Any licensed vision professional'}},
        {
          category: 'preventive',
          label: 'Routine eye exam',
          values: {'vision-600': '$25 copay · plan pays 100% · 1 per year'},
        },
        {
          category: 'preventive',
          label: 'Exam includes',
          values: {'vision-600': 'Physician exam, visual acuity, glaucoma test, refraction'},
        },
        {
          category: 'materials',
          label: 'Frames & lenses',
          values: {'vision-600': '$0 copay · plan pays 100%'},
        },
        {
          category: 'materials',
          label: 'Lens types',
          values: {'vision-600': 'Single, bifocal, trifocal, progressive, lenticular'},
        },
        {category: 'materials', label: 'Contacts', values: {'vision-600': 'Conventional or disposable'}},
        {
          category: 'materials',
          label: 'Coatings',
          values: {'vision-600': 'Anti-scratch + anti-reflective'},
        },
        {category: 'materials', label: 'Hardware waiting period', values: {'vision-600': '90 days'}},
        {category: 'plan', label: 'Not covered', values: {'vision-600': 'Lasik · cosmetic services'}},
      ],
    },
  ],
  plans: [
    {
      id: 'preventive-plus',
      category: 'medical',
      name: 'Preventive Plus',
      tagline: 'ACA preventive + virtual care starter',
      highlights: ['ACA-compliant', '$0 prescriptions', 'Clever Health 24/7'],
      rates: {employee: 17.54, employeeSpouse: 42.46, employeeChildren: 35.08, family: 57.46},
      network: 'FirstHealth PPO',
      sbcUrl: cornerstonePlusSbc,
      accentColor: '#38bdf8',
    },
    {
      id: 'preventive-bronze',
      category: 'medical',
      name: 'Preventive Bronze',
      tagline: 'Adds office visits + accident + hospitalization',
      highlights: ['Specialist + urgent care', 'Accident up to $500', 'Hospitalization day 1+'],
      rates: {employee: 31.85, employeeSpouse: 78.46, employeeChildren: 64.85, family: 106.62},
      network: 'FirstHealth PPO',
      sbcUrl: cornerstoneBronzeSbc,
      accentColor: '#b45309',
    },
    {
      id: 'preventive-silver',
      category: 'medical',
      name: 'Preventive Silver',
      tagline: 'Higher caps + chiropractic coverage',
      highlights: ['10 chiro visits', 'Accident up to $1,000', '$750/day hospital'],
      rates: {employee: 39.92, employeeSpouse: 99.23, employeeChildren: 81.92, family: 135.23},
      network: 'FirstHealth PPO',
      sbcUrl: cornerstoneSilverSbc,
      accentColor: '#94a3b8',
    },
    {
      id: 'preventive-gold',
      category: 'medical',
      name: 'Preventive Gold',
      tagline: 'Most coverage — best for higher utilizers',
      highlights: ['15 chiro visits', 'Accident up to $2,000', '$1,000/day hospital'],
      rates: {employee: 45.69, employeeSpouse: 114.69, employeeChildren: 93.46, family: 156.46},
      network: 'FirstHealth PPO',
      sbcUrl: cornerstoneGoldSbc,
      accentColor: '#d4af37',
    },
    {
      id: 'dental-1000',
      category: 'dental',
      name: 'Dental — Preventive, Basic & Major',
      tagline: '$1,000 annual max · $50 deductible',
      highlights: ['Preventive 100%', 'Basic 80% (90-day wait)', 'Major 50% (180-day wait)'],
      rates: {employee: 7.85, employeeSpouse: 15.05, employeeChildren: 13.25, family: 19.85},
      accentColor: '#0d9488',
    },
    {
      id: 'vision-600',
      category: 'vision',
      name: 'Vision — Routine + Hardware',
      tagline: '$600 annual max · $25 exam copay',
      highlights: ['No deductible', 'Frames + contacts covered', '$0 copay on hardware'],
      rates: {employee: 3.92, employeeSpouse: 7.25, employeeChildren: 6.42, family: 9.46},
      accentColor: '#2563eb',
    },
  ],
  coverageCards: [
    {
      icon: 'stethoscope',
      color: '#16a34a',
      title: 'Preventive Care',
      bullets: [
        'ACA-compliant: 55+ covered preventive services',
        '$0-copay virtual care, unlimited usage',
        'Prescription program with $0 generics',
        'FirstHealth PPO network',
      ],
    },
    {
      icon: 'shield-check',
      color: '#2563eb',
      title: 'Dental',
      bullets: [
        'Preventive services covered 100%',
        'Basic services 80% after 90-day waiting period',
        'Major services 50% after 180-day waiting period',
        '$1,000 annual maximum',
      ],
    },
    {
      icon: 'eye',
      color: '#0d9488',
      title: 'Vision',
      bullets: [
        '$25 copay for routine exam',
        'Frames, lenses, contacts covered after 90-day waiting period',
        'Anti-scratch + AR coatings included',
        '$600 annual combined max (exam + hardware)',
      ],
    },
  ],
  resourcesSection: {
    intro:
      'Download the Employee Enrollment Guide, the four Summary-of-Benefits PDFs (Plus / Bronze / Silver / Gold), and the Cornerstone Caregiving FAQ.',
    items: [
      {
        icon: 'resources',
        title: 'Plan Resources',
        description: 'Employee guide, SBCs, FAQ, and compliance notices.',
        href: '/cornerstone/resources',
      },
      {
        icon: 'guide',
        title: '2026 Employee Enrollment Guide',
        description: 'The full PDF with rates, plan details, and FAQ.',
        href: cornerstoneEnrollmentGuide,
        external: true,
      },
    ],
  },
  resourcesPage: {
    intro:
      'Download the Employee Enrollment Guide and SBCs for each medical plan. These documents include full benefit details and the legally required disclosures for your coverage.',
    documents: [
      {
        title: 'Employee Enrollment Guide',
        description:
          'Full 2026 guide — plan descriptions, rates, dental, vision, FAQ, prescription formulary.',
        href: cornerstoneEnrollmentGuide,
      },
      {
        title: 'SBC — Preventive Plus',
        description: 'Summary of Benefits & Coverage for the Preventive Plus medical plan.',
        href: cornerstonePlusSbc,
      },
      {
        title: 'SBC — Preventive Bronze',
        description: 'Summary of Benefits & Coverage for the Preventive Bronze medical plan.',
        href: cornerstoneBronzeSbc,
      },
      {
        title: 'SBC — Preventive Silver',
        description: 'Summary of Benefits & Coverage for the Preventive Silver medical plan.',
        href: cornerstoneSilverSbc,
      },
      {
        title: 'SBC — Preventive Gold',
        description: 'Summary of Benefits & Coverage for the Preventive Gold medical plan.',
        href: cornerstoneGoldSbc,
      },
      {
        title: 'Managers FAQ',
        description: 'Internal-facing FAQ for branch and HR managers.',
        href: cornerstoneManagersFaq,
      },
      {
        title: 'Cornerstone Caregiving Flyer (2026)',
        description: 'Single-page member-facing benefits flyer.',
        href: cornerstoneFlyer,
      },
    ],
    virtualCare: {
      title: 'Clever Health — Smart Virtual Care',
      body: 'All medical plans include $0-copay 24/7 virtual urgent care through the <app>Clever Health app</app> — cold/flu, allergy, eye/ear/mouth, injuries, medication refills, and more.',
      closing: 'Download the <app>Clever Health app</app> on iOS or Android to get started.',
    },
    disclosures: [
      {
        label: 'COBRA continuation',
        text: 'You may continue coverage after a qualifying event. Contact HR for details and election forms.',
      },
      {
        label: 'CHIPRA notice',
        text: 'Premium-assistance opportunities through Medicaid + CHIP. See your local office for the state-specific notice.',
      },
      {
        label: 'Medicare Part D',
        text: 'Prescription drug coverage offered through these Plans is non-creditable relative to Part D.',
      },
      {
        label: 'HIPAA Rights',
        text: 'Your HIPAA Rights Notice is available from HR.',
      },
    ],
  },
  confirmation: {
    effectiveDateNote:
      'Your coverage will go into effect approximately 10 days after your first payroll deduction.',
    whatHappensNext: [
      {
        title: 'Your benefits are submitted',
        body: 'Your coverage goes into effect on a Monday, 10 days after your first payroll deduction. Your local office processes your elections for payroll setup — check with them for your payroll-deduction schedule and any specifics to your location.',
      },
      {
        title: 'Your ID cards are electronic',
        body: "You won't get a physical card in the mail. Once your coverage is active, access your digital cards in the <app>Clever Health app</app> — download it on iOS or Android, then register to set up your credentials.",
      },
      {
        title: 'Register on the member portal',
        body: 'Visit <url>ctpa.com</url>, open the Members tab, and register as an Employee/Insured to review your benefits and claims.',
      },
    ],
    faq: [
      {
        question: 'When does my coverage start?',
        answer:
          'Once your payroll deductions begin, coverage goes into effect on a Monday, 10 days from your first deduction. Confirm your payroll-deduction schedule with your local office.',
      },
      {
        question: 'When will I get my ID card and how do I access plan details?',
        answer:
          "Your ID cards are electronic — you won't receive one in the mail. Once your coverage is active, access your digital cards in the <app>Clever Health app</app> (download it, then register to set up your credentials). For benefits and claims, you can also register at <url>ctpa.com</url> (Members → Register → Employee/Insured).",
      },
      {
        question: 'Which doctor network should I use?',
        answer:
          "Your plan uses the First Health Network (PPO) — you'll pay the least by staying in-network. Find a provider at <url>firsthealthlbp.com</url> or call First Health at <tel>1-800-226-5116</tel>.",
      },
      {
        question: 'How does my prescription benefit work?',
        answer:
          'Generic and formulary brand medications are covered at $0 copay; non-formulary drugs are available via a discount card. Fill at participating retail pharmacies. For prescription questions, call the Concierge Team at <tel>1-888-820-5687</tel>, who will connect you with the right service.',
      },
      {
        question: 'How do I use virtual care?',
        answer:
          'All medical plans include $0-copay, 24/7 virtual care through the <app>Clever Health app</app> — download on iOS or Android to get started.',
      },
      {
        question: 'Can I change my benefits after enrolling?',
        answer:
          "Once enrolled, you can't make changes until the next Open Enrollment, unless you have a qualifying life event (marriage, birth, loss of other coverage, etc.). Contact your local office for details and your HIPAA Rights Notice.",
      },
      {
        question: 'Who do I contact with questions?',
        answer:
          'Call Concierge Administrative Services at <tel>1-888-820-5687</tel> or visit <url>ctpa.com</url>. For anything policy-specific, check with your local office.',
      },
    ],
  },
  faq: [
    {
      label: 'Enrolling',
      items: [
        {
          question: 'When can I enroll?',
          answer: 'There are 3 different times you could enroll:',
          bullets: [
            'Upon hire — you have 3 weeks to enroll or decline coverage. If you decline, you must wait until Open Enrollment in December unless you have a qualifying life event.',
            "Open Enrollment — the first 3 weeks of December each year. If you didn't sign up when first hired, or want to make changes, you can do so now; changes take effect the following January.",
            "Qualifying life event — the only exception allowing you to sign up after Open Enrollment and the 3 weeks after your hire date. You'll be asked to provide documented proof of the event, including the member's name and the date it occurred. See “What is a qualifying life event?” below.",
          ],
        },
        {
          question: 'How do I enroll?',
          answer: 'Want to enroll in the MEC, Dental, or Vision plans? Follow these steps:',
          bullets: [
            'Visit <url>enrollment.ctpa.com/cornerstone</url>.',
            'Review and/or download the Enrollment Guide to see all available benefit options.',
            "When you're ready to enroll, click Enroll Now.",
            'Enter your five-digit AxisCare Code and click Submit.',
            'Choose the benefits you want, add them to the cart, complete the member form, and submit.',
          ],
        },
      ],
    },
    {
      label: 'Coverage & Cards',
      items: [
        {
          question: "What happens if I'm not picking up hours? How long will I maintain coverage?",
          answer:
            "Concierge has a 5-week grace period where you'll maintain coverage. If you're out of work for more than 5 weeks, your coverage will terminate.",
        },
        {
          question: 'After I enroll, how quickly will I have coverage?',
          answer:
            'Once you see the deductions for insurance come out of your paycheck, coverage goes into effect on a Monday, 10 days from the first deduction date. If you have been off assignment for 13 or more weeks, you will be treated like a re-hire and offered the opportunity to enroll.',
        },
        {
          question: 'Will I receive my insurance cards in the mail?',
          answer:
            "All cards are virtual. Once your coverage is active, you can access your digital cards through the <app>Clever Health app</app> on your phone. After downloading the app, you'll need to register to set up your credentials.",
        },
        {
          question: 'Do I need to select dental, vision, and health insurance?',
          answer:
            "No. If you only need dental, for example, you don't need to elect vision and health coverage as well. Your coverage is customizable to your needs.",
        },
      ],
    },
    {
      label: 'Providers & Changes',
      items: [
        {
          question: 'How do I locate a provider?',
          answer: 'Where you look depends on the plan:',
          bullets: [
            'Medical & RX — benefits are provided within the First Health Network for in-network discounts. Find participating providers using the <app>Clever Health app</app> or at <url>firsthealthlbp.com</url>. For prescriptions, review the Enrollment Guide formulary for Unlimited ACA and $0-copay medications; C3Rx is the network and can be reached at <tel>866-330-8780</tel>.',
            'Dental & Vision — these plans have no network, so any licensed dental or vision professional can render covered services. To verify a practice accepts the benefits before your appointment, have the provider contact Member Services at <tel>888-820-5687</tel> or <email>cs@ctpa.com</email>.',
          ],
        },
        {
          question: 'How do I drop coverage?',
          answer:
            'Members who wish to drop coverage mid-year for themselves or their dependents must contact <email>eligibility@ctpa.com</email> to have coverage terminated. Once coverage is dropped, you may not add it again until the next Open Enrollment unless a qualifying event occurs. Termination may take up to one pay period to reflect on your paycheck.',
        },
      ],
    },
    {
      label: 'Qualifying Life Events',
      items: [
        {
          question: 'What is a qualifying life event?',
          answer: 'There are 4 basic types of qualifying life events (this is not a full list):',
          bullets: [
            "Loss of health coverage in the past 31 days — losing existing coverage, or losing coverage through a parent's plan at 26. You have 60 days to report an event if you or a dependent becomes eligible for, or loses, Medicaid/CHIP.",
            'Changes in household in the past 31 days — getting married or divorced, having a baby, or a death in the family of someone covered by your plan.',
            'Changes in residence — moving to a different ZIP code or county, a student moving to or from school, or moving to or from a shelter or other transitional housing.',
          ],
          closing:
            'For the full list of qualifying events, contact the Concierge Team or your local office.',
        },
      ],
    },
  ],
  howToEnroll: HOW_TO_ENROLL_WITH_CODE,
  checkout: {
    collectDateOfHire: true,
    phoneLabel: 'Cell phone',
  },
};
